import { Inject, Injectable, InjectionToken, Optional } from '@angular/core';

import { TranslationsProvider } from '../i18n.service';
import {
  LanguageConfig,
  LANGUAGE_CONFIG,
  LOCALE,
  PhraseAppTranslations,
  Translations,
} from '../i18n.tokens';
import { PhraseAppEditorService } from './phrase-app-editor.service';

export const PHRASE_APP_TRANSLATIONS = new InjectionToken<PhraseAppTranslations[]>('Translations');

type TranslationsRepository = {
  en: () => Promise<{ en: Record<string, any> }>;
  de: () => Promise<{ de: Record<string, any> }>;
  fr: () => Promise<{ fr: Record<string, any> }>;
  nl: () => Promise<{ nl: Record<string, any> }>;
  sv: () => Promise<{ sv: Record<string, any> }>;
  fi: () => Promise<{ fi: Record<string, any> }>;
};

export type SupportedTranslations = keyof TranslationsRepository;

export async function phraseAppTranslationsLoader(
  _locale: SupportedTranslations,
  defaultLocale: SupportedTranslations,
  repositories: TranslationsRepository[],
): Promise<PhraseAppTranslations[]> {
  const locales: SupportedTranslations[] = [defaultLocale];
  if (_locale !== defaultLocale) {
    locales.push(_locale);
  }

  const translations = repositories.reduce(
    (acc, repo) => [...acc, ...locales.map(locale => repo[locale]())],
    [],
  );

  return Promise.all(translations);
}

@Injectable()
export class PhraseAppTranslationService extends TranslationsProvider {
  public locale: string;
  public hasEditor: boolean;
  public defaultLocale: SupportedTranslations;
  public availableLocales: string[];
  public cache: Record<string, string> = {};

  private translations: PhraseAppTranslations;
  private readonly defaultLocaleTranslations: PhraseAppTranslations;

  constructor(
    @Inject(LOCALE) private localeFromAdmin: string,
    @Inject(LANGUAGE_CONFIG) private languageConfig: LanguageConfig,
    @Optional()
    @Inject(PHRASE_APP_TRANSLATIONS)
    translations: PhraseAppTranslations[],
    // required to create editor svc instance
    private editor: PhraseAppEditorService,
  ) {
    super();
    this.defaultLocale = languageConfig.defaultLanguage;
    this.availableLocales = languageConfig.languages;
    this.setLocale(localeFromAdmin || this.defaultLocale);
    if (translations) {
      this.translations = this.mergeTranslations(translations);
      this.defaultLocaleTranslations = this.translations;
    }

    this.hasEditor = this.editor.installed;
  }

  public lookup(key: string, locale: string): string | null {
    const cached = this.cache[key];
    if (cached) return cached;

    let translation: any = this.translations[locale];
    const keys: string[] = key.split('.');

    while (keys.length) {
      translation = translation[keys.shift()!];
      if (translation === undefined || translation === null) {
        break;
      }
    }

    if (typeof translation !== 'string') {
      return null;
    }

    this.cache[key] = translation;
    return translation;
  }

  public markForEditor(key: string): string {
    return `{{__phrase_${key}__}}`;
  }

  public setLocale(locale: string): boolean {
    const canSet = this.availableLocales.includes(locale);
    if (canSet) {
      this.locale = locale;
      this.localeChanged.next(locale);
    }

    return canSet;
  }

  public setTranslations(translations: Translations[]): void {
    this.invalidateCache();
    this.translations = this.mergeTranslations(translations);
    if (!this.translations[this.defaultLocale]) {
      this.translations[this.defaultLocale] = this.defaultLocaleTranslations;
    }
  }

  private mergeTranslations(translations: Translations[]): Translations {
    let groupedTranslations: Record<string, any[]> = {};

    for (const translation of translations) {
      Object.keys(translation).forEach(locale => {
        if (groupedTranslations[locale]) {
          groupedTranslations = {
            ...groupedTranslations,
            [locale]: [...groupedTranslations[locale], translation[locale]],
          };
        } else {
          groupedTranslations[locale] = [translation[locale]];
        }
      });
    }

    return Object.entries(groupedTranslations).reduce(
      (acc, [key, value]) => ({
        ...acc,
        [key]: value.reduce((_acc, _value) => ({ ..._acc, ..._value }), {}),
      }),
      {},
    );
  }

  private invalidateCache(): void {
    this.cache = {};
  }
}
